import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MatterDetailed } from '../../../../types/matters';
import { matterApi } from '../../../../config/api';
import { useMailClientStore } from '../../../edgemail-client/store/mailClientStore';
import {
  ArrowDownLeftIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpRightIcon,
  DocumentIcon,
  EnvelopeIcon,
  FunnelIcon,
  PaperClipIcon,
  ShieldCheckIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import AttachmentPreviewModal from '../../../edgemail-client/components/reading-pane/AttachmentPreviewModal';
import MessageBody from '../../../edgemail-client/components/reading-pane/MessageBody';
import {
  downloadAttachmentBlob,
  fetchMessage,
  getAttachmentDownloadUrl,
} from '../../../edgemail-client/services/MailClientAPI';
import type { MailAttachment, MailMessage } from '../../../edgemail-client/types/mail.types';

type ChannelFilter = 'all' | 'email' | 'sms';
type DetailFilter = 'all' | 'unread' | 'privileged' | 'attachments' | 'needs_repair';

interface CommunicationItem {
  id: string;
  type: 'email' | 'sms';
  date: string | null;
  subject: string;
  preview: string;
  from: { name: string; address: string };
  to: { name: string; address: string }[];
  direction: 'inbound' | 'outbound';
  confidentiality: 'standard' | 'privileged' | 'work-product';
  source_id: string;
  mailbox_id: string;
  folder_id: string;
  folder_path: string | null;
  has_attachments: boolean;
  attachment_count: number;
  is_read: boolean;
  thread_id: string | null;
  link_status: 'linked' | 'folder_unlinked' | 'conflict';
}

interface CommunicationHealth {
  status: 'healthy' | 'needs_repair' | 'conflict' | 'syncing' | 'attention' | 'not_configured';
  matterFolderId: string | null;
  matterFolderPath: string | null;
  linkedFolderCount: number;
  folderUnlinkedCount: number;
  conflictingLinkCount: number;
  automationEnabled: boolean;
  ruleActive: boolean;
  backfillStatus: string | null;
  lastMovedCount: number;
  lastBackfillJobId: string | null;
  staleBackfill: boolean;
  message: string;
}

interface CommunicationsResponse {
  matterId: string;
  matterNumber: string | null;
  communications: CommunicationItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  counts: {
    email: number;
    sms: number;
    unread: number;
    privileged: number;
    attachments: number;
    linked: number;
    folderUnlinked: number;
    conflicts: number;
  };
  channels: Array<{
    id: 'email' | 'sms';
    label: string;
    status: 'active' | 'coming_soon';
    count: number;
  }>;
  health: CommunicationHealth;
}

interface CommunicationsTabProps {
  matter: MatterDetailed;
}

const PAGE_SIZE = 50;

function formatDateLabel(dateStr: string | null): string {
  if (!dateStr) return 'No date';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return 'No date';
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function groupByDate(items: CommunicationItem[]): Record<string, CommunicationItem[]> {
  return items.reduce<Record<string, CommunicationItem[]>>((groups, item) => {
    const label = formatDateLabel(item.date);
    groups[label] = groups[label] || [];
    groups[label].push(item);
    return groups;
  }, {});
}

function getCommunicationStatus(health: CommunicationHealth, emailCount: number) {
  if (health.conflictingLinkCount > 0 || health.status === 'conflict') {
    return {
      title: 'Review filing',
      detail: `${health.conflictingLinkCount} email${health.conflictingLinkCount === 1 ? '' : 's'} may be filed to another matter.`,
      label: 'Needs review',
      labelClass: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
    };
  }

  if (health.folderUnlinkedCount > 0 || health.status === 'needs_repair') {
    return {
      title: 'Finish filing',
      detail: `${health.folderUnlinkedCount} email${health.folderUnlinkedCount === 1 ? '' : 's'} are in this engagement folder but not attached to the engagement record yet.`,
      label: 'Action available',
      labelClass: 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300',
    };
  }

  if (health.status === 'syncing' || health.status === 'attention' || health.staleBackfill) {
    return {
      title: 'Communications available',
      detail: `${emailCount} email${emailCount === 1 ? '' : 's'} are available here. WeBeMail is still checking older mailbox history in the background.`,
      label: 'Background check',
      labelClass: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    };
  }

  if (health.status === 'not_configured') {
    return {
      title: 'Auto-filing not set up',
      detail: 'Emails can still be linked manually. Turn on engagement communications to file future mail automatically.',
      label: 'Optional',
      labelClass: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    };
  }

  return {
    title: 'Communications ready',
    detail: `${emailCount} email${emailCount === 1 ? '' : 's'} are attached to this engagement.`,
    label: 'Ready',
    labelClass: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  };
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const CommunicationsTab: React.FC<CommunicationsTabProps> = ({ matter }) => {
  const navigate = useNavigate();
  const { setActiveAccountId, setActiveFolder, selectMessage } = useMailClientStore();
  const [data, setData] = useState<CommunicationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [filter, setFilter] = useState<DetailFilter>('all');
  const [selectedCommunicationId, setSelectedCommunicationId] = useState<string | null>(null);
  const [previewMessage, setPreviewMessage] = useState<MailMessage | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const loadCommunications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await matterApi.getCommunications(matter.id, {
        page,
        limit: PAGE_SIZE,
        channel,
        filter,
      }) as CommunicationsResponse;
      setData(response);
    } catch {
      setError('Failed to load engagement communications.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [matter.id, page, channel, filter]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    matterApi.getCommunications(matter.id, {
      page,
      limit: PAGE_SIZE,
      channel,
      filter,
    })
      .then((response) => {
        if (active) setData(response as CommunicationsResponse);
      })
      .catch(() => {
        if (active) {
          setError('Failed to load engagement communications.');
          setData(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [matter.id, page, channel, filter]);

  const handleOpenEmail = useCallback((item: CommunicationItem) => {
    if (item.type !== 'email' || !item.mailbox_id) return;
    setActiveAccountId(item.mailbox_id);
    if (item.folder_id) setActiveFolder(item.folder_id);
    selectMessage(item.id);
    navigate('/email');
  }, [navigate, selectMessage, setActiveAccountId, setActiveFolder]);

  const handleRepair = useCallback(async () => {
    setRepairing(true);
    setActionMessage(null);
    setError(null);
    try {
      const result = await matterApi.repairCommunicationsLinkage(matter.id) as {
        repairedCount?: number;
        conflictingCount?: number;
      };
      setActionMessage(`${result.repairedCount || 0} message${result.repairedCount === 1 ? '' : 's'} linked to this engagement.`);
      await loadCommunications();
    } catch {
      setError('Could not repair engagement email linkage.');
    } finally {
      setRepairing(false);
    }
  }, [loadCommunications, matter.id]);

  const resetFilters = useCallback((nextChannel: ChannelFilter) => {
    setChannel(nextChannel);
    setFilter('all');
    setPage(1);
  }, []);

  const communications = data?.communications || [];
  const totalPages = data?.totalPages || 0;
  const grouped = groupByDate(communications);
  const health = data?.health;
  const status = health ? getCommunicationStatus(health, data?.counts.email || 0) : null;
  const selectedCommunication = communications.find((item) => item.id === selectedCommunicationId) || null;

  useEffect(() => {
    if (selectedCommunicationId && !communications.some((item) => item.id === selectedCommunicationId)) {
      setSelectedCommunicationId(null);
      setPreviewMessage(null);
      setPreviewError(null);
    }
  }, [communications, selectedCommunicationId]);

  useEffect(() => {
    let active = true;

    if (!selectedCommunication || selectedCommunication.type !== 'email' || !selectedCommunication.mailbox_id) {
      setPreviewMessage(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return () => { active = false; };
    }

    setPreviewLoading(true);
    setPreviewError(null);
    fetchMessage(selectedCommunication.mailbox_id, selectedCommunication.id)
      .then((message) => {
        if (active) setPreviewMessage(message);
      })
      .catch(() => {
        if (active) {
          setPreviewMessage(null);
          setPreviewError('Preview unavailable. Open the message in WeBeMail to inspect the source email.');
        }
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });

    return () => { active = false; };
  }, [selectedCommunication]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Communications</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Engagement-linked email is live. SMS is reserved for the upcoming messaging integration.
          </p>
        </div>
        <button
          type="button"
          onClick={loadCommunications}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <button
          type="button"
          onClick={() => resetFilters('all')}
          className={`flex items-center justify-between rounded-lg border p-4 text-left transition-colors ${
            channel === 'all' || channel === 'email'
              ? 'border-lex-accent bg-lex-accent-soft'
              : 'border-gray-200 bg-white hover:border-lex-accent dark:border-gray-700 dark:bg-gray-800/60'
          }`}
        >
          <span className="flex items-center gap-3">
            <EnvelopeIcon className="h-5 w-5 text-lex-accent" />
            <span>
              <span className="block text-sm font-semibold text-gray-900 dark:text-white">Email</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">Linked and folder-associated messages</span>
            </span>
          </span>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">{data?.counts.email || 0}</span>
        </button>

        <button
          type="button"
          onClick={() => resetFilters('sms')}
          className={`flex items-center justify-between rounded-lg border p-4 text-left transition-colors ${
            channel === 'sms'
              ? 'border-lex-accent bg-lex-accent-soft'
              : 'border-gray-200 bg-white hover:border-lex-accent dark:border-gray-700 dark:bg-gray-800/60'
          }`}
        >
          <span className="flex items-center gap-3">
            <SparklesIcon className="h-5 w-5 text-violet-500" />
            <span>
              <span className="block text-sm font-semibold text-gray-900 dark:text-white">SMS</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">Coming soon</span>
            </span>
          </span>
          <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300">Stub</span>
        </button>
      </div>

      {health && status && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.labelClass}`}>
                {status.label}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{status.title}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{status.detail}</span>
              {health.matterFolderPath && (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Folder: {health.matterFolderPath}
                </span>
              )}
            </div>
            {(health.folderUnlinkedCount > 0 || health.status === 'needs_repair') && (
              <button
                type="button"
                onClick={handleRepair}
                disabled={repairing}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <WrenchScrewdriverIcon className="h-4 w-4" />
                {repairing ? 'Filing...' : 'Finish filing'}
              </button>
            )}
          </div>
        </div>
      )}

      {actionMessage && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
          {actionMessage}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <FunnelIcon className="h-4 w-4 text-gray-400" />
        {[
          ['all', 'All', data?.total || 0],
          ['unread', 'Unread', data?.counts.unread || 0],
          ['privileged', 'Privileged', data?.counts.privileged || 0],
          ['attachments', 'Has files', data?.counts.attachments || 0],
          ['needs_repair', 'Needs repair', data?.counts.folderUnlinked || 0],
        ].map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            onClick={() => {
              setFilter(id as DetailFilter);
              setPage(1);
            }}
            disabled={channel === 'sms' && id !== 'all'}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === id
                ? 'bg-lex-accent text-lex-accent-contrast'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            } disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {label} <span className="ml-1 opacity-75">{count}</span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-lg bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && !error && channel === 'sms' && (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center dark:border-gray-700">
          <SparklesIcon className="mx-auto mb-3 h-10 w-10 text-violet-400" />
          <h4 className="text-base font-semibold text-gray-900 dark:text-white">SMS will plug into this same timeline</h4>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
            The tab is intentionally reserved now so engagement communications can support text messaging without adding notes as a false association.
          </p>
        </div>
      )}

      {!loading && !error && channel !== 'sms' && communications.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-700">
          <EnvelopeIcon className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">No communications found</h4>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
            Link emails from WeBeMail or enable engagement auto-filing to populate this timeline.
          </p>
        </div>
      )}

      {!loading && !error && channel !== 'sms' && communications.length > 0 && (
        <div className="grid min-h-[520px] gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(380px,0.95fr)]">
          <div className="min-w-0 space-y-4">
            {Object.entries(grouped).map(([dateLabel, items]) => (
              <div key={dateLabel}>
                <div className="mb-3 flex items-center gap-3">
                  <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                  <span className="whitespace-nowrap text-xs font-medium text-gray-500 dark:text-gray-400">{dateLabel}</span>
                  <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                </div>
                <div className="space-y-2">
                  {items.map((item) => (
                    <CommunicationCard
                      key={item.id}
                      item={item}
                      selected={item.id === selectedCommunicationId}
                      onSelect={setSelectedCommunicationId}
                    />
                  ))}
                </div>
              </div>
            ))}

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={page === 1}
                  className="rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  disabled={page >= totalPages}
                  className="rounded-md bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          <CommunicationPreviewPanel
            item={selectedCommunication}
            message={previewMessage}
            loading={previewLoading}
            error={previewError}
            onClose={() => setSelectedCommunicationId(null)}
            onOpenInWeBeMail={handleOpenEmail}
          />
        </div>
      )}
    </div>
  );
};

function CommunicationCard({
  item,
  selected,
  onSelect,
}: {
  item: CommunicationItem;
  selected: boolean;
  onSelect: (messageId: string) => void;
}) {
  const DirectionIcon = item.direction === 'outbound' ? ArrowUpRightIcon : ArrowDownLeftIcon;
  const directionLabel = item.direction === 'outbound' ? 'OUT' : 'IN';
  const directionClass = item.direction === 'outbound'
    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
    : 'bg-lex-accent-soft text-lex-accent';
  const participants = item.direction === 'outbound'
    ? item.to.map((target) => target.name || target.address).filter(Boolean).join(', ')
    : item.from.name || item.from.address;

  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      className={`group flex w-full gap-3 rounded-lg border p-4 text-left transition-colors ${
        selected
          ? 'border-lex-accent bg-lex-accent-soft shadow-sm'
          : item.is_read
          ? 'border-gray-200 bg-white hover:border-lex-accent dark:border-gray-700 dark:bg-gray-800/50'
          : 'border-lex-accent bg-lex-accent-soft'
      }`}
    >
      <div className="pt-0.5">
        <EnvelopeIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex min-w-0 items-center gap-2">
          <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold ${directionClass}`}>
            <DirectionIcon className="h-3 w-3" />
            {directionLabel}
          </span>
          <span className={`truncate text-sm ${item.is_read ? 'text-gray-800 dark:text-gray-200' : 'font-semibold text-gray-900 dark:text-white'}`}>
            {item.subject}
          </span>
        </div>
        <p className="mb-1 truncate text-xs text-gray-500 dark:text-gray-400">
          {item.direction === 'outbound' ? 'To' : 'From'}: {participants || 'Unknown sender'}
        </p>
        {item.preview && (
          <p className="line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{item.preview}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {item.folder_path && (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300">
              {item.folder_path}
            </span>
          )}
          {item.link_status === 'folder_unlinked' && (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
              Needs link repair
            </span>
          )}
          {item.link_status === 'conflict' && (
            <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:text-red-300">
              Linked elsewhere
            </span>
          )}
          {item.has_attachments && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
              <PaperClipIcon className="h-3.5 w-3.5" />
              {item.attachment_count}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">{formatTime(item.date)}</span>
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500" />
        </div>
        {item.confidentiality !== 'standard' && (
          <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300">
            <ShieldCheckIcon className="h-3 w-3" />
            {item.confidentiality === 'privileged' ? 'PRIVILEGED' : 'WORK PRODUCT'}
          </span>
        )}
      </div>
    </button>
  );
}

function CommunicationPreviewPanel({
  item,
  message,
  loading,
  error,
  onClose,
  onOpenInWeBeMail,
}: {
  item: CommunicationItem | null;
  message: MailMessage | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onOpenInWeBeMail: (item: CommunicationItem) => void;
}) {
  if (!item) {
    return (
      <aside className="hidden min-h-[520px] rounded-xl border border-dashed border-gray-300 bg-white/60 p-8 text-center dark:border-gray-700 dark:bg-gray-900/40 xl:flex xl:flex-col xl:items-center xl:justify-center">
        <EnvelopeIcon className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Select an email to preview</h4>
        <p className="mt-2 max-w-xs text-xs text-gray-500 dark:text-gray-400">
          The source message stays one click away without leaving the engagement workspace.
        </p>
      </aside>
    );
  }

  const attachmentCount = message?.attachments?.length ?? item.attachment_count ?? 0;

  return (
    <aside className="flex min-h-[520px] min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="h-4 w-1 rounded-full bg-lex-accent" />
            <h4 className="truncate text-base font-semibold text-gray-900 dark:text-white">{item.subject}</h4>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {item.folder_path || 'Engagement email'} {item.date ? `- ${formatDateLabel(item.date)} ${formatTime(item.date)}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          aria-label="Close preview"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="border-b border-gray-200 px-5 py-3 dark:border-gray-700">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 text-sm">
            <p className="truncate font-semibold text-gray-900 dark:text-white">
              {item.from.name || item.from.address || 'Unknown sender'}
              {item.from.address && <span className="font-normal text-gray-500 dark:text-gray-400">&lt;{item.from.address}&gt;</span>}
            </p>
            <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
              To: {item.to.map((recipient) => recipient.name || recipient.address).filter(Boolean).join(', ') || 'Unknown recipient'}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {item.confidentiality !== 'standard' && (
              <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-1 text-[10px] font-bold text-amber-700 dark:text-amber-300">
                <ShieldCheckIcon className="h-3 w-3" />
                {item.confidentiality === 'privileged' ? 'Privileged' : 'Work product'}
              </span>
            )}
            {attachmentCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                <PaperClipIcon className="h-3 w-3" />
                {attachmentCount}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
        {loading && (
          <div className="space-y-3 animate-pulse">
            <div className="h-4 w-3/4 rounded bg-gray-100 dark:bg-gray-800" />
            <div className="h-4 w-full rounded bg-gray-100 dark:bg-gray-800" />
            <div className="h-4 w-5/6 rounded bg-gray-100 dark:bg-gray-800" />
          </div>
        )}
        {!loading && error && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-300">
            {error}
          </div>
        )}
        {!loading && !error && message && (
          <MessageBody html={message.bodyHtml} text={message.bodyText || item.preview} />
        )}
        {!loading && !error && !message && (
          <p className="text-sm text-gray-500 dark:text-gray-400">{item.preview || 'No preview content is available.'}</p>
        )}
      </div>

      {!loading && !error && message && message.attachments.length > 0 && (
        <CommunicationAttachmentStrip attachments={message.attachments} />
      )}

      <div className="border-t border-gray-200 px-5 py-3 dark:border-gray-700">
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => onOpenInWeBeMail(item)}
            className="inline-flex items-center gap-2 rounded-lg bg-lex-accent px-3 py-1.5 text-xs font-semibold text-lex-accent-contrast hover:bg-lex-accent-hover"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            Open in WeBeMail
          </button>
        </div>
      </div>
    </aside>
  );
}

function CommunicationAttachmentStrip({ attachments }: { attachments: MailAttachment[] }) {
  const [previewAttachmentId, setPreviewAttachmentId] = useState<string | null>(null);

  const handleOpen = async (attachment: MailAttachment) => {
    const win = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const url = await getAttachmentDownloadUrl(attachment.id);
      if (url.startsWith('/')) {
        const blob = await downloadAttachmentBlob(attachment.id);
        const objectUrl = URL.createObjectURL(blob);
        if (win) {
          win.location.href = objectUrl;
        } else {
          window.open(objectUrl, '_blank', 'noopener,noreferrer');
        }
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 15000);
        return;
      }

      if (win) {
        win.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      if (win) win.close();
    }
  };

  const handleSave = async (attachment: MailAttachment) => {
    try {
      const url = await getAttachmentDownloadUrl(attachment.id);
      const link = document.createElement('a');

      if (url.startsWith('/')) {
        const blob = await downloadAttachmentBlob(attachment.id);
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = attachment.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 15000);
        return;
      }

      link.href = url;
      link.download = attachment.filename;
      link.rel = 'noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // Keep preview usable even if an individual attachment action fails.
    }
  };

  return (
    <div className="border-t border-gray-200 px-5 py-3 dark:border-gray-700">
      <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
        Attachments ({attachments.length})
      </p>
      <div className="flex flex-wrap gap-2">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="flex max-w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800"
          >
            <DocumentIcon className="h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500" />
            <button
              type="button"
              onClick={() => setPreviewAttachmentId(attachment.id)}
              className="min-w-0 text-left"
              title={attachment.filename}
            >
              <p className="max-w-[180px] truncate text-xs font-medium text-gray-800 dark:text-gray-100">
                {attachment.filename}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {formatBytes(attachment.size)}
              </p>
            </button>
            <div className="ml-1 flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPreviewAttachmentId(attachment.id)}
                className="rounded px-2 py-1 text-[11px] font-medium text-lex-accent hover:bg-lex-accent-soft"
              >
                Preview
              </button>
              <button
                type="button"
                onClick={() => handleOpen(attachment)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                aria-label={`Open ${attachment.filename}`}
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handleSave(attachment)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                aria-label={`Save ${attachment.filename}`}
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {previewAttachmentId && (
        <AttachmentPreviewModal
          open={Boolean(previewAttachmentId)}
          attachments={attachments}
          initialAttachmentId={previewAttachmentId}
          onClose={() => setPreviewAttachmentId(null)}
        />
      )}
    </div>
  );
}

export default CommunicationsTab;
